import React, { FC } from 'react';
import styles from '../journey-explorer.module.scss';
import { FlowDirection, FlowWindow, JourneyMeta } from '../../../../models/journey-explorer/journey-explorer';
import { FLOW_STEP_OPTIONS, FLOW_WINDOWS, FlowSettings, flowWindowLabel } from '../journey-explorer-utils';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';

interface FlowControlsProps {
	meta: JourneyMeta;
	lookup: Map<string, InteractionInfo>;
	settings: FlowSettings;
	active: boolean;
	onChange: (patch: Partial<FlowSettings>) => void;
}

const FlowControls: FC<FlowControlsProps> = ({ meta, lookup, settings, active, onChange }) => {
	const anchorLabel = settings.anchor ? resolveInfo(lookup, settings.anchor).label : '';
	const between = settings.direction === 'between';
	let stepsLabel = 'over the next';
	if (between) {
		stepsLabel = 'over up to';
	} else if (settings.direction === 'before') {
		stepsLabel = 'over the previous';
	}

	const interactionOptions = (exclude: string | null) =>
		meta.interaction_types.map((type) => (
			<optgroup key={type.type} label={type.label}>
				{type.interactions
					.filter((interaction) => interaction.name !== exclude)
					.map((interaction) => (
						<option key={interaction.name} value={interaction.name}>
							{interaction.label || interaction.name}
						</option>
					))}
			</optgroup>
		));

	return (
		<div className={`${styles.flow_controls} ${active ? styles.flow_controls_active : ''}`}>
			<span>Show what customers do</span>
			<div className={styles.flow_controls_pair}>
				<select
					className={styles.flow_select}
					aria-label="Direction"
					value={settings.direction}
					onChange={(event) => onChange({ direction: event.target.value as FlowDirection })}
				>
					<option value="after">after</option>
					<option value="before">before</option>
					<option value="between">between</option>
				</select>
				<select
					className={styles.flow_select}
					aria-label="Interaction"
					value={settings.anchor ?? ''}
					onChange={(event) => onChange({ anchor: event.target.value })}
				>
					{!settings.anchor && <option value="" disabled />}
					{interactionOptions(null)}
				</select>
			</div>
			{between && (
				<div className={styles.flow_controls_row}>
					<span>and</span>
					<select
						className={styles.flow_select}
						aria-label="End point"
						value={settings.end ?? ''}
						onChange={(event) => onChange({ end: event.target.value })}
					>
						{!settings.end && <option value="" disabled />}
						{interactionOptions(settings.anchor)}
					</select>
				</div>
			)}
			<div className={styles.flow_controls_row}>
				<span>{stepsLabel}</span>
				<select
					className={`${styles.flow_select} ${styles.flow_select_narrow}`}
					aria-label="Interactions"
					value={settings.steps}
					onChange={(event) => onChange({ steps: Number(event.target.value) })}
				>
					{FLOW_STEP_OPTIONS.map((steps) => (
						<option key={steps} value={steps}>
							{steps}
						</option>
					))}
				</select>
				<span>interactions</span>
			</div>
			<div className={styles.flow_controls_row}>
				<span>within</span>
				<select
					className={styles.flow_select}
					aria-label="Time window"
					value={settings.within}
					onChange={(event) => onChange({ within: event.target.value as FlowWindow })}
				>
					{FLOW_WINDOWS.map(({ key, label, note }) => (
						<option key={key} value={key}>
							{note ? `${label}  (${note})` : label}
						</option>
					))}
				</select>
			</div>
			<span className={styles.flow_controls_note}>
				{settings.within === 'any'
					? 'No time limit between steps.'
					: `Every step must happen within ${flowWindowLabel(settings.within)} of ${anchorLabel}.`}
			</span>
			<label
				className={styles.flow_apply}
				title="Map, Funnel, Steps, KPIs and Journey paths only count this part of each journey, for customers who did the starting interaction"
			>
				<input
					type="checkbox"
					role="switch"
					className={styles.flow_apply_switch}
					checked={!!settings.applyToAll}
					onChange={(event) => onChange({ applyToAll: event.target.checked })}
				/>
				Apply to all views
			</label>
		</div>
	);
};

export default FlowControls;
